import { toaster } from '@/core/ui';
import {
  DeleteWeeklyRecordReviewsDocument,
  UpdateWeeklyRecordReviewDocument,
  UpdateWeeklyRecordReviewsStateDocument,
  WeeklyRecordReviewsDocument,
} from '@/graphql/gql/graphql';
import { gql, useMutation as useApolloMutation } from '@/libs/apollo/export';

export const useWeeklyRecordReviewMutations = (): {
  updateLoading: boolean;
  deleteLoading: boolean;
  bulkStateLoading: boolean;
  handleStateChange: (reviewId: string, val: number | null) => Promise<void>;
  handleActionedByChange: (reviewId: string, val: number | null) => Promise<void>;
  handleMembersChange: (reviewId: string, keys: Set<string> | Iterable<string>) => Promise<void>;
  handleDelete: (reviewId: string | number) => Promise<void>;
  handleCommentSave: (reviewId: string, comment: string) => Promise<void>;
  handleBulkStateChange: (ids: string[], state: number) => Promise<boolean>;
  handleMultiDelete: (ids: (string | number)[]) => Promise<boolean>;
} => {
  const [updateMutation, { loading: updateLoading }] = useApolloMutation(UpdateWeeklyRecordReviewDocument, {
    // refetchQueries: [WeeklyRecordReviewsDocument],
    update(cache, _, { variables }) {
      if (!variables) return;
      const { id, input } = variables;

      const reviewId = cache.identify({ __typename: 'WeeklyRecordReview', id });
      if (!reviewId) return;

      if (input.comment !== undefined) {
        cache.writeFragment({
          id: reviewId,
          fragment: gql`
            fragment UpdatedComment on WeeklyRecordReview {
              comment
            }
          `,
          data: { comment: input.comment },
        });
      }

      if (input.actionedBy !== undefined) {
        cache.writeFragment({
          id: reviewId,
          fragment: gql`
            fragment UpdatedActionedBy on WeeklyRecordReview {
              actionedBy
            }
          `,
          data: { actionedBy: input.actionedBy },
        });
      }

      if (input.reason !== undefined) {
        cache.writeFragment({
          id: reviewId,
          fragment: gql`
              fragment UpdatedReason on WeeklyRecordReview {
                reason
              }
            `,
          data: { reason: input.reason },
        });
      }

      if (input.state !== undefined) {
        cache.writeFragment({
          id: reviewId,
          fragment: gql`
              fragment UpdatedState on WeeklyRecordReview {
                state
              }
            `,
          data: { state: input.state },
        });
      }

      if (input.members !== undefined) {
        cache.writeFragment({
          id: reviewId,
          fragment: gql`
              fragment UpdatedMembers on WeeklyRecordReview {
                members {
                  userId
                  role
                  user {
                    id
                    firstName
                    displayName
                    username
                  }
                }
              }
            `,
          data: { members: input.members },
        });
      }
    },
  });

  const [deleteMutation, { loading: deleteLoading }] = useApolloMutation(DeleteWeeklyRecordReviewsDocument, {
    // refetchQueries: [WeeklyRecordReviewsDocument],
    update(cache, _, { variables }) {
      if (!variables) return;
      const { ids = [] } = variables as { ids: string[] };
      if (ids.length === 0) return;
      ids.forEach((id) => {
        cache.evict({
          id: cache.identify({ __typename: 'WeeklyRecordReview', id }),
        });
      });
    },
  });

  const [bulkStateMutation, { loading: bulkStateLoading }] = useApolloMutation(
    UpdateWeeklyRecordReviewsStateDocument,
    { refetchQueries: [WeeklyRecordReviewsDocument] },
  );

  const handleStateChange = async (reviewId: string, val: number | null) => {
    if (!reviewId || val == null) return;
    try {
      await updateMutation({
        variables: { id: reviewId, input: { state: val } },
      });
      toaster.success('State updated');
    } catch (err: unknown) {
      toaster.error(err instanceof Error ? err.message : 'Failed to update state');
    }
  };

  const handleActionedByChange = async (reviewId: string, val: number | null) => {
    if (!reviewId || val == null) return;
    try {
      await updateMutation({
        variables: { id: reviewId, input: { actionedBy: val } },
      });
      toaster.success('Actioned by updated');
    } catch (err: unknown) {
      toaster.error(err instanceof Error ? err.message : 'Failed to update actioned by');
    }
  };

  const handleMembersChange = async (reviewId: string, keys: Set<string> | Iterable<string>) => {
    if (!reviewId || !keys) return;
    const currentIds = Array.from(keys) as (string | number)[];
    try {
      await updateMutation({
        variables: {
          id: String(reviewId),
          input: {
            members: currentIds.map((userId) => ({ userId: Number(userId), role: 1 })),
          },
        },
      });
      toaster.success('Members updated');
    } catch (err: unknown) {
      toaster.error(err instanceof Error ? err.message : 'Failed to update members');
    }
  };

  const handleDelete = async (reviewId: string | number) => {
    try {
      const res = await deleteMutation({
        variables: { ids: [String(reviewId)] },
      });
      if (res.data?.restricted?.deleteWeeklyRecordReviews) {
        toaster.success('Review deleted');
      } else {
        toaster.error('Failed to delete review');
      }
    } catch (err: unknown) {
      toaster.error(err instanceof Error ? err.message : 'Failed to delete review');
    }
  };

  const handleCommentSave = async (reviewId: string, comment: string) => {
    try {
      await updateMutation({
        variables: { id: reviewId, input: { comment } },
      });
      toaster.success('Comment updated');
    } catch (err: unknown) {
      toaster.error(err instanceof Error ? err.message : 'Failed to update comment');
    }
  };

  const handleBulkStateChange = async (ids: string[], state: number): Promise<boolean> => {
    if (ids.length === 0) return false;
    try {
      const res = await bulkStateMutation({
        variables: { ids, state },
      });
      if (res.data?.restricted?.updateWeeklyRecordReviewsState) {
        toaster.success(`State updated for ${ids.length} review${ids.length === 1 ? '' : 's'}`);
        return true;
      }
      toaster.error('Failed to update state');
      return false;
    } catch (err: unknown) {
      toaster.error(err instanceof Error ? err.message : 'Failed to update state');
      return false;
    }
  };

  const handleMultiDelete = async (ids: (string | number)[]) => {
    if (!ids.length) return false;
    try {
      const res = await deleteMutation({
        variables: { ids: ids.map(String) },
      });
      if (res.data?.restricted?.deleteWeeklyRecordReviews) {
        toaster.success(`${ids.length} review${ids.length === 1 ? '' : 's'} deleted`);
        return true;
      }
      toaster.error('Failed to delete selected reviews');
      return false;
    } catch (err: unknown) {
      toaster.error(err instanceof Error ? err.message : 'Failed to delete reviews');
      return false;
    }
  };

  return {
    updateLoading,
    deleteLoading,
    bulkStateLoading,
    handleStateChange,
    handleActionedByChange,
    handleMembersChange,
    handleDelete,
    handleCommentSave,
    handleBulkStateChange,
    handleMultiDelete,
  };
};
